/**
 * useStreamViewTracking — LEGACY (mount-based) stream-view tracking.
 *
 * This is the older path, kept alive for existing mount-based surfaces
 * (StreamTheater / LiveHubPage / ChallengesSection / StreamingSection). New
 * surfaces use `useTrackWatchSession` (the accessible play-state model in
 * StreamInlinePlayer) — do NOT build new surfaces on this hook.
 *
 * Manages the lifecycle of a stream view: creates a view record when
 * the theater opens, closes it when the theater closes. Handles edge
 * cases like browser navigation (beforeunload) and component unmount.
 *
 * Usage:
 *   const { onTheaterOpen, onTheaterClose } = useStreamViewTracking();
 *   // Call onTheaterOpen when theater opens with stream data
 *   // Call onTheaterClose when theater closes
 */

import {useCallback, useRef, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import { StreamingApi, StreamingPlatform, StreamViewEventBody, StreamViewEventBodyActionEnum, Token } from "@devour/client";
import getConfig from "@/utils/getConfig";

interface StreamViewContext {
    /*
     * `StreamingPlatform` — the generated type its callers actually hold — and
     * OPTIONAL, matching its source. A roster member need not have a channel,
     * and platform is a property of a channel.
     *
     * It was a hand-written `"TWITCH" | "YOUTUBE" | "KICK"`, which is narrower
     * than `StreamingPlatform` in both directions: it could not be undefined,
     * and it had no `MULTI`. Callers pass `FeaturedStreamResponse.platform` /
     * `LiveStreamItem.platform` straight in, so the hand-written version was
     * making a claim about the data that the data does not make.
     *
     * A view event with no SINGLE platform is not sent at all — see below. That
     * is deliberate: the alternative is defaulting to one, and an analytics row
     * claiming a viewer watched on Twitch when the stream was MULTI or had no
     * platform at all is worse than a missing row. A gap is honest; a fabricated
     * row is not, and afterwards it is indistinguishable from a real one.
     */
    platform?: StreamingPlatform;
    channelId: string;
    surfaceId?: string;
    competitionId?: string;
}

/**
 * React hook that tracks stream viewing sessions for the Streak Engine.
 *
 * Call `onTheaterOpen` when a stream theater opens. If called while a previous
 * view is still active, the hook automatically ends the prior session first.
 * Call `onTheaterClose` when the theater closes.
 *
 * The hook also registers a `beforeunload` listener so sessions are closed
 * even if the user navigates away mid-stream.
 */
export function useStreamViewTracking() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const activeViewId = useRef<string | null>(null);
    const tokenRef = useRef<Token | undefined>(fullToken ?? undefined);
    const startedAtRef = useRef<number | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    /*
     * Watch-time now accrues server-side from heartbeats, so this hook must send
     * them while a view is open — start/end alone would record zero seconds. This
     * is the legacy (mount-based) tracking path still used by StreamTheater /
     * LiveHubPage / ChallengesSection / StreamingSection; the accessible
     * play-state model lives in StreamInlinePlayer for the newer surfaces.
     */
    const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const stopHeartbeat = useCallback(() => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    }, []);

    /*
     * Sequence counter prevents race conditions when onTheaterOpen is called
     * rapidly (e.g. switching streams). Each call increments the counter; only
     * the latest call's response is accepted. Stale responses are discarded.
     */
    const openSeqRef = useRef(0);

    const startTimer = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        startedAtRef.current = Date.now();
        setElapsedSeconds(0);
        intervalRef.current = setInterval(() => {
            if (startedAtRef.current) {
                setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
            }
        }, 1000);
    }, []);

    const stopTimer = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        stopHeartbeat();
        startedAtRef.current = null;
        setElapsedSeconds(0);
    }, [stopHeartbeat]);

    /** Clear interval without triggering a React state update (safe for beforeunload/unmount) */
    const clearTimerSilently = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        stopHeartbeat();
        startedAtRef.current = null;
    }, [stopHeartbeat]);

    /*
     * Keep token ref in sync so beforeunload always uses the latest token
     * without causing the useEffect to re-run and prematurely close views
     */
    useEffect(() => {
        tokenRef.current = fullToken ?? undefined;
    }, [fullToken?.token]);

    const sendViewEvent = useCallback(async (body: StreamViewEventBody): Promise<string | null> => {
        if (!tokenRef.current?.token) return null;

        try {
            const data = await new StreamingApi(getConfig(tokenRef.current)).createStreamsViewEvent({
                streamViewEventBody: body,
            });
            return data.viewId || null;
        } catch {
            return null;
        }
    }, []);

    const onTheaterOpen = useCallback(async (context: StreamViewContext) => {
        const seq = ++openSeqRef.current;

        // Close any existing view first
        if (activeViewId.current) {
            await sendViewEvent({action: StreamViewEventBodyActionEnum.end, viewId: activeViewId.current});
            activeViewId.current = null;
        }

        // If another onTheaterOpen was called while we awaited, abandon this one
        if (seq !== openSeqRef.current) return;

        /*
         * No single platform, no event. `/streams/view-event` attributes a view
         * to ONE platform, and neither `undefined` nor `MULTI` is one — there is
         * no honest value to put in the field, so no row is written.
         */
        const platform = context.platform === StreamingPlatform.TWITCH ||
            context.platform === StreamingPlatform.YOUTUBE ||
            context.platform === StreamingPlatform.KICK
            ? context.platform
            : undefined;
        if (!platform) return;

        const viewId = await sendViewEvent({
            action: StreamViewEventBodyActionEnum.start,
            platform,
            channelId: context.channelId,
            surfaceId: context.surfaceId,
            competitionId: context.competitionId,
        });

        // Check again after the start call returns
        if (seq !== openSeqRef.current) return;

        activeViewId.current = viewId;
        if (viewId) {
            startTimer();
            stopHeartbeat();
            heartbeatRef.current = setInterval(() => {
                if (activeViewId.current) {
                    void sendViewEvent({action: StreamViewEventBodyActionEnum.heartbeat, viewId: activeViewId.current});
                }
            }, 30_000);
        }
    }, [sendViewEvent, startTimer, stopHeartbeat]);

    const onTheaterClose = useCallback(async () => {
        if (!activeViewId.current) return;

        stopTimer();
        await sendViewEvent({action: StreamViewEventBodyActionEnum.end, viewId: activeViewId.current});
        activeViewId.current = null;
    }, [sendViewEvent, stopTimer]);

    /*
     * Handle page unload + component unmount — send end event via fetch+keepalive.
     * Uses tokenRef (not fullToken) so the effect only runs on mount/unmount,
     * preventing token refreshes from prematurely closing active views.
     */
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (!activeViewId.current || !tokenRef.current?.token) return;

            const config = getConfig(tokenRef.current);
            /*
             * keepalive: true is required here for reliable delivery on page unload
             * The StreamingApi client cannot be used in this context
             */
            window.fetch(`${config.basePath}/streams/view-event`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(config.headers as Record<string, string>),
                },
                body: JSON.stringify({action: "end", viewId: activeViewId.current}),
                keepalive: true,
            }).catch(() => {}); // fire-and-forget
            activeViewId.current = null;
            clearTimerSilently();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            // Component unmount — close any active view
            if (activeViewId.current) {
                handleBeforeUnload();
            }
            clearTimerSilently();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {onTheaterOpen, onTheaterClose, elapsedSeconds};
}
